import React, { useEffect, useMemo } from 'react';
import { Combobox } from '@withjoy/joykit';
import type { ComboboxOption } from '@withjoy/joykit/components/Combobox/Combobox.types';
import type { LanguagePickerV2Props } from './LanguagePickerV2.types';
import { formatLanguage, formatUnknownLanguage } from './LanguagePickerV2.utils.internal';
import { normalizeLanguageCode } from './LanguagePickerV2.utils';
import { useTranslation } from '@shared/core';
import i18n from '@shared/core/i18n/i18n';
import { useLanguageOptions } from './useLanguageOptions.internal';
import { addError } from '@shared/utils/logger';
import { IconUnion } from '@withjoy/joykit/icons';

export const LanguagePickerV2: React.FC<LanguagePickerV2Props> = props => {
  const {
    disabled,
    placeholder,
    mode,
    name,
    onBlur,
    onOpenChange,
    allowedLanguageCodes,
    triggerVariant,
    searchable = true,
    buttonProps: buttonPropsOverride,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-errormessage': ariaErrorMessage,
  } = props;
  const { tq } = useTranslation('languagePickerV2');
  const allLanguageOptions = useLanguageOptions();
  const languageOptions = useMemo(
    () => (allowedLanguageCodes ? allLanguageOptions.filter(option => allowedLanguageCodes.includes(option.value)) : allLanguageOptions),
    [allLanguageOptions, allowedLanguageCodes],
  );

  const selectedCodes = useMemo(
    () => (props.multiple ? props.value.map(code => normalizeLanguageCode(code)) : [normalizeLanguageCode(props.value)]),
    [props.multiple, props.value],
  );

  // Options mode (vs children) so Combobox renders selected-state indicators itself
  const { options, selectedDisplays, selectedFlags } = useMemo(() => {
    const displays: string[] = [];
    const flags: string[] = [];

    const items: ComboboxOption[] = languageOptions.map(lang => {
      const formatted = formatLanguage(lang.value, lang.flag, lang.label);

      if (selectedCodes.includes(lang.value)) {
        displays.push(formatted.display);
        flags.push(formatted.flag);
      }

      return { value: lang.value, label: formatted.display, keywords: [lang.label, lang.value] };
    });

    return { options: items, selectedDisplays: displays, selectedFlags: flags };
  }, [languageOptions, selectedCodes]);

  // Count from selectedCodes (not matched displays) so stored codes missing from the
  // options list still surface; a lone unknown code falls back to its raw value.
  const selectedCount = props.multiple ? selectedCodes.length : props.value ? 1 : 0;
  const displayValue =
    selectedCount > 1
      ? tq.plural(selectedCount, {
          one: tq`${String(selectedCount)} languages`,
          other: tq`${String(selectedCount)} languages`,
        })
      : selectedCount === 1
        ? (selectedDisplays[0] ?? formatUnknownLanguage(selectedCodes[0], i18n.language))
        : '';

  // Flag variant collapses the trigger to a Globe icon + flag (single selection
  // only); an unknown stored code has no flag, so the icon alone marks the control.
  const isFlagTrigger = triggerVariant === 'flag' && !props.multiple;
  const triggerDisplayValue = isFlagTrigger ? (selectedFlags[0] ?? '') : displayValue;

  const hasUnknownValue = selectedCount > 0 && selectedDisplays.length < selectedCount;
  useEffect(() => {
    if (hasUnknownValue) {
      addError(`Invalid language value(s) in LanguagePickerV2: ${selectedCodes.join(', ')}`, { selectedCodes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnknownValue]);

  const sharedProps = {
    options,
    placeholder: placeholder || tq`Select language...`,
    displayValue: triggerDisplayValue,
    disabled,
    name,
    onBlur,
    onOpenChange,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-errormessage': ariaErrorMessage,
    searchPlaceholder: tq`Search languages...`,
    emptyMessage: tq`No languages found.`,
    searchable,
  };

  if (mode === 'button') {
    const buttonProps = { intent: 'neutral', shape: 'rounded', ...buttonPropsOverride, ...(isFlagTrigger && { startIcon: <IconUnion size={16} /> }) } as const;
    return props.multiple ? (
      <Combobox
        mode="button"
        multiple
        buttonProps={buttonProps}
        dialogTitle={tq`Choose Languages`}
        value={selectedCodes}
        onValueChange={props.onChange}
        {...sharedProps}
      />
    ) : (
      <Combobox
        mode="button"
        buttonProps={buttonProps}
        dialogTitle={tq`Choose Language`}
        value={selectedCodes[0]}
        onValueChange={props.onChange}
        {...sharedProps}
      />
    );
  }

  return props.multiple ? (
    <Combobox
      multiple
      value={selectedCodes}
      onValueChange={props.onChange}
      {...sharedProps}
    />
  ) : (
    <Combobox
      value={selectedCodes[0]}
      onValueChange={props.onChange}
      {...sharedProps}
    />
  );
};

LanguagePickerV2.displayName = 'LanguagePickerV2';
